/**
 * ArenaRenderer — draws the map the simulation is actually using.
 *
 * The geometry here comes from the same `MapDef` the predictor collides against,
 * so what you see is what you stand on. v1 drew a decorative 200x200 ground quad
 * and let the AI spawn entities near it; the client's idea of the floor and the
 * server's were related only by convention.
 */

import * as THREE from 'three/webgpu';
import {
  createSurfaceMaterial,
  panelRepeatFor,
  setMaterialTheme,
  materialThemeColorShift,
  clearMaterialCache,
} from './ToonMaterials.js';
import {
  createLavaShaderMaterial,
  createDeckMaterial,
  createPlatformRimMaterial,
  clearPlatformRimMaterials,
  clearConveyorMaterials,
  getChaosOverlayMaterials,
  registerSurfaceFrameHook,
} from './SurfaceShaders.js';
import {
  initInstancedBatchManager,
  isInstanceable,
  addInstancedPlatform,
  clearAllBatches,
} from './entities/InstancedBatchManager.js';
import { initParticles, updateSkyColors, selectParticleType } from './EnvironmentEffects.js';
import { isProp } from './props/TradingFloorProps.js';
import { beginProps, addProp, flushProps, clearProps } from './props/PropBatcher.js';
import { getMapMeta } from './maps/index.js';

/**
 * How far BELOW `floor.y` the lava plane is drawn.
 *
 * Lava is the one floor kind still drawn as decoration over other geometry, so it
 * is the one that needs the dip: coplanar surfaces leave the depth buffer nothing
 * to order by and the ground reads as horizontal striping — worse on the WebGL2
 * fallback, whose depth buffer is coarser. The solid deck no longer needs it,
 * because the square collision slab it used to sit over is gone; that deck IS the
 * collision surface now and takes a smaller lift of its own (see `_buildFloor`).
 *
 * 0.05 rather than 0.001: the camera is near 0.1 / far 1200, which leaves roughly
 * 0.01 units of depth resolution at the far edge of the arena, and the step is
 * invisible at a scale where a player is two units tall.
 */
const FLOOR_DEPTH_EPSILON = 0.05;

/**
 * Where the chaos overlay learns which mutators are live.
 *
 * In production this is the predictor's world: `chaos_event` and the snapshot
 * `chaos` ledger now carry mutation payloads, the predictor folds them into
 * `world.chaos.applied`, and its constructor publishes the world through
 * `__chaosPredictedWorld` — so the floor shows ice/conveyor/lava for exactly
 * the window the physics changed. A global handle rather than an import,
 * matching the gym/debug probes below, because the predictor must stay free of
 * the three/webgpu stack this module drags in. No source means no overlay
 * rather than a crash.
 */
let _chaosWorldSource = () =>
  globalThis.__chaosPredictedWorld?.() ??
  globalThis.gym?.game?.predictor?.world ??
  globalThis.chaos?.game?.predictor?.world ??
  null;

export function setChaosWorldSource(fn) {
  _chaosWorldSource = fn;
}

export class ArenaRenderer {
  constructor(scene) {
    this.scene = scene;
    this.root = new THREE.Group();
    this.root.name = 'arena';
    scene.add(this.root);
    initInstancedBatchManager(scene);
    this._floorMesh = null;
    this._chaosGround = null;
    this._chaosQuads = [];
    this._chaosKind = null;
    registerSurfaceFrameHook((delta) => this._tickChaos(delta));
  }

  /** Builds every mesh for one map. Called once per match. */
  build(map) {
    this.clear();
    const meta = getMapMeta(map.id);
    setMaterialTheme(meta.theme);

    this._buildFloor(map);

    beginProps();
    for (const platform of map.platforms) {
      // A prop REPLACES the box, it does not decorate one: drawing both would
      // leave a grey slab sticking out of every desk. The platform still exists
      // for the sim, which is the point — art changed, collision did not.
      //
      // Queued rather than added: a furnished arena holds a few hundred props
      // and drawing each one as its own Group cost 2,200 draw calls. See
      // PropBatcher — they all land as a handful of InstancedMeshes below.
      if (platform.model && isProp(platform.model)) {
        // Props are authored with their base at y=0; the platform is stored by
        // its centre, so the base is one half-height below it.
        //
        // The rotation is visual only. Collision is an AABB, so a map may not
        // rotate a prop off the axes without the box and the model disagreeing
        // — quarter turns are the only safe values and all any arena uses.
        addProp(platform.model, platform.x, platform.y - platform.hy, platform.z, platform.rotY ?? 0);
        continue;
      }

      if (isInstanceable(platform)) {
        addInstancedPlatform(platform);
      } else {
        this.root.add(this._buildPlatformMesh(platform));
      }
      const rim = this._buildPlatformRim(platform, map.floor);
      if (rim) this.root.add(rim);
    }

    flushProps(this.root);

    for (const zone of map.windZones) this.root.add(this._buildWindZone(zone));

    this._buildChaosOverlays(map);

    updateSkyColors(null, null, meta.sky);
    initParticles(this.scene, selectParticleType(map.floor.kind, { skyPreset: meta.sky }));
  }

  _buildFloor(map) {
    const floor = map.floor;
    if (floor.kind === 'none') return;

    if (floor.kind === 'lava') {
      // Sized to the arena rather than a fixed 200: the fog and the zone wall
      // are what define the edge of the world, and a floor that outruns both
      // reads as an empty plain.
      const size = map.startRadius * 2.6;
      const geo = new THREE.PlaneGeometry(size, size, 24, 24);
      const mesh = new THREE.Mesh(geo, createLavaShaderMaterial());
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(map.centerX, floor.y - FLOOR_DEPTH_EPSILON, map.centerZ);
      mesh.userData.ownsMaterial = true;
      this.root.add(mesh);
      this._floorMesh = mesh;
      return;
    }

    // Drawn at `floorRadius`, which is exactly where `onArenaFloor` says there
    // is ground — this circle IS the collision surface, not a decoration over
    // one. The map used to carry a square collision slab underneath as well,
    // and its corners stuck out past the drawn circle, so the honest edge and
    // the standable edge disagreed; the slab is gone and this is the only
    // answer to "where can I stand". Lifted 0.02 rather than the full epsilon:
    // at 0.05 the deck visibly buried character feet.
    const geo = new THREE.CircleGeometry(map.floorRadius, 96);
    const mesh = new THREE.Mesh(
      geo,
      createDeckMaterial({
        arenaRadius: map.startRadius,
        centerX: map.centerX,
        centerZ: map.centerZ,
        colorShift: materialThemeColorShift(),
      })
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(map.centerX, floor.y + 0.02, map.centerZ);
    mesh.receiveShadow = true;
    mesh.userData.ownsMaterial = true;
    this.root.add(mesh);
    this._floorMesh = mesh;
  }

  _buildPlatformMesh(platform) {
    const geo = new THREE.BoxGeometry(platform.hx * 2, platform.hy * 2, platform.hz * 2);
    const mesh = new THREE.Mesh(
      geo,
      // Same tiling rule as the instanced path: whether a platform is instanced
      // is a draw-call decision and must not change how its surface looks.
      createSurfaceMaterial(platform.surface, panelRepeatFor(Math.max(platform.hx, platform.hz) * 2))
    );
    mesh.position.set(platform.x, platform.y, platform.z);
    mesh.castShadow = platform.solid;
    mesh.receiveShadow = true;
    mesh.name = platform.id;
    // Conveyors are the one surface built per platform rather than pulled from
    // the shared cache, so they are the one whose material this mesh owns —
    // including its cloned scrolling texture (`ownsMap`). The normal map stays
    // shared and must never be disposed here.
    mesh.userData.ownsMaterial = platform.surface.kind === 'conveyor';
    mesh.userData.ownsMap = platform.surface.kind === 'conveyor';
    return mesh;
  }

  /**
   * The luminous edge line around a platform's top face — the cue you judge a
   * jump against. Skipped for decking at floor level (the arena deck already
   * owns the ground read) and for anything a player cannot stand on.
   */
  _buildPlatformRim(platform, floor) {
    if (!platform.solid) return null;
    const top = platform.y + platform.hy;
    const floorY = floor.kind === 'none' ? -Infinity : floor.y;
    if (top <= floorY + 0.25) return null;

    const geo = new THREE.PlaneGeometry(platform.hx * 2, platform.hz * 2);
    const mesh = new THREE.Mesh(geo, createPlatformRimMaterial(platform.hx, platform.hz));
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(platform.x, top + 0.02, platform.z);
    mesh.renderOrder = 1;
    // Material lives in the rim cache — clear() must not dispose it per-mesh.
    mesh.userData.ownsMaterial = false;
    return mesh;
  }

  /**
   * Hidden meshes for the floor mutators, one arena circle plus a quad per
   * plain platform — built with the arena so an activation mid-fight only
   * toggles visibility. The sim's rule (mutations.ts `chaosSurface`): a chaos
   * floor covers the arena floor and NORMAL/ICE platforms; bounce pads,
   * speed strips and authored conveyors keep their own mechanics, so they
   * keep their own look. Raised platforms are also deliberately excluded
   * from LAVA — they are the refuge, and painting them would say "lethal".
   */
  _buildChaosOverlays(map) {
    getChaosOverlayMaterials().radius.value = map.startRadius;
    this._chaosQuads = [];
    this._chaosGround = null;
    this._chaosKind = null;

    if (map.floor.kind === 'solid') {
      const geo = new THREE.CircleGeometry(map.floorRadius, 96);
      const mesh = new THREE.Mesh(geo, getChaosOverlayMaterials().ice);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(map.centerX, map.floor.y + 0.032, map.centerZ);
      mesh.visible = false;
      mesh.userData.ownsMaterial = false;
      this.root.add(mesh);
      this._chaosGround = mesh;
    }

    const floorY = map.floor.kind === 'none' ? -Infinity : map.floor.y;
    for (const platform of map.platforms) {
      if (!platform.solid) continue;
      const kind = platform.surface.kind;
      if (kind !== 'normal' && kind !== 'ice') continue;
      const top = platform.y + platform.hy;
      if (top <= floorY + 0.25) continue;
      const geo = new THREE.PlaneGeometry(platform.hx * 2, platform.hz * 2);
      const mesh = new THREE.Mesh(geo, getChaosOverlayMaterials().ice);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(platform.x, top + 0.015, platform.z);
      mesh.visible = false;
      mesh.userData.ownsMaterial = false;
      this.root.add(mesh);
      this._chaosQuads.push(mesh);
    }
  }

  /**
   * Runs every render frame off SurfaceShaders' hook. Reads the predictor
   * world's chaos ledger (entries are active while tick ∈ [tick, untilTick))
   * and drives the shared overlay materials: which kind shows where, the
   * conveyor's direction, and a short fade so activations pop in rather than
   * teleport in.
   */
  _tickChaos(delta) {
    if (!this._chaosGround && this._chaosQuads.length === 0) return;
    const mats = getChaosOverlayMaterials();
    const world = _chaosWorldSource?.();

    let floorMut = null;
    let lavaLive = false;
    if (world?.chaos) {
      for (const e of world.chaos.applied) {
        if (world.tick < e.tick || world.tick >= e.untilTick) continue;
        const k = e.mutation.kind;
        if (k === 'ice_floor' || k === 'conveyor_floor') floorMut = e.mutation;
        else if (k === 'lava_floor') lavaLive = true;
      }
    }

    const quadKind = floorMut ? (floorMut.kind === 'ice_floor' ? 'ice' : 'conveyor') : null;
    // The ground shows the deadliest truth: lava beats a slick surface.
    const groundKind = lavaLive ? 'lava' : quadKind;

    if (floorMut?.kind === 'conveyor_floor') {
      // Same heading convention as the sim (sin, cos), so the chevrons point
      // exactly where the floor pushes.
      const rad = (floorMut.directionDeg * Math.PI) / 180;
      mats.conveyorDir.value.set(Math.sin(rad), Math.cos(rad));
      mats.conveyorSpeed.value = floorMut.driftSpeed;
    }

    const active = groundKind !== null;
    const s = mats.strength;
    const target = active ? 1 : 0;
    s.value += (target - s.value) * Math.min(1, delta * 6);
    if (Math.abs(target - s.value) < 0.02) s.value = target;
    const anyVisible = s.value > 0.01;

    // Material swaps only on kind changes; visibility only on fade edges. The
    // quad latch survives the fade-out so a lava-only spell (quads off) does
    // not flash stale quads while the ground fades.
    if (active && this._chaosKind !== groundKind) {
      this._chaosKind = groundKind;
      if (this._chaosGround) this._chaosGround.material = mats[groundKind];
      if (quadKind) for (const quad of this._chaosQuads) quad.material = mats[quadKind];
    }
    if (active) this._quadsOn = quadKind !== null;
    if (this._chaosGround) this._chaosGround.visible = anyVisible;
    const quadsVisible = anyVisible && !!this._quadsOn;
    for (const quad of this._chaosQuads) quad.visible = quadsVisible;
  }

  _buildWindZone(zone) {
    // Volume marker only. The wind itself is applied by the sim on both sides;
    // this is here so a player can see why they are being pushed.
    const geo = new THREE.BoxGeometry(zone.hx * 2, zone.hy * 2, zone.hz * 2);
    const mat = new THREE.MeshBasicNodeMaterial({
      color: 0x9fd0ff,
      transparent: true,
      opacity: 0.08,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(zone.x, zone.y, zone.z);
    mesh.renderOrder = -2;
    mesh.userData.ownsMaterial = true;
    return mesh;
  }

  clear() {
    clearProps(this.root);
    clearAllBatches();
    clearConveyorMaterials();
    clearPlatformRimMaterials();
    for (let i = this.root.children.length - 1; i >= 0; i--) {
      const child = this.root.children[i];
      this.root.remove(child);
      // Geometry is per-mesh and always safe to drop. Materials are only safe
      // when this mesh built its own: the shared surface materials live in
      // ToonMaterials' cache, and disposing one here would leave every other
      // platform of that kind pointing at a dead pipeline.
      // `traverse` rather than a direct dispose: a prop is a Group whose
      // geometry hangs off its children, so disposing the Group freed nothing
      // and every arena rebuild leaked a desk's worth of buffers.
      child.traverse((o) => o.geometry?.dispose());
      if (child.userData.ownsMaterial) {
        // material.dispose() does not free textures — the per-conveyor cloned
        // scroll texture leaked one GPU texture per conveyor per match.
        if (child.userData.ownsMap) child.material?.map?.dispose();
        child.material?.dispose();
      }
    }
    // Every mesh referencing the cached surface materials is gone as of the
    // loop above, so the per-theme cache can be dropped too; without this,
    // every theme visited in a session left its materials on the GPU forever.
    clearMaterialCache();
    this._floorMesh = null;
    this._chaosGround = null;
    this._chaosQuads = [];
    this._chaosKind = null;
    this._quadsOn = false;
  }
}
